/**
 * Reconciles track-record entries that exist ONLY on this device up to the
 * server.
 *
 * WHY A CLIENT-SIDE RECONCILIATION — a SQL migration cannot do this job:
 * until `investor_track_record` shipped there was no server column for these
 * records at all. Onboarding wrote them into the `inverio-profile` Zustand
 * store (AsyncStorage) and sent only `portfolio_count` to the DB. The data
 * lives in users' devices, so the app is the only process that can reach it.
 *
 * That also means each device holds a DIFFERENT, overlapping half of the
 * history — the direct cause of "my portfolio differs on mobile vs web". Every
 * device runs this, and the natural-key upsert makes the halves converge on
 * one row per investment instead of duplicating.
 *
 * WHY RECONCILE RATHER THAN A ONE-SHOT "already migrated" FLAG:
 * a flag has a hole that loses data silently. Consider an investor whose shell
 * mounts before they finish onboarding: the flag is set against an empty
 * history, and if a later onboarding write then fails, nothing ever retries —
 * the records sit on the device forever while the server shows none. Comparing
 * local against server instead makes the operation self-healing: whatever is
 * missing gets uploaded, whenever it appears, however it got missed. It costs
 * nothing extra because the server list is already fetched for display.
 *
 * THE ORDERING HAZARD, which is the reason for the `_hasHydrated` guard:
 * the store rehydrates from AsyncStorage asynchronously, so reading
 * `investmentHistory` too early yields `[]` — indistinguishable from "this
 * investor has no history". Acting on that reads as "nothing to do". Same
 * loading-gate class as P0-4 (`isLoading` treated as "no entitlement").
 */

import { useEffect, useRef } from 'react';

import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import { useProfileStore } from '@/lib/state/profile-store';
import type { InvestmentRecord } from '@/lib/state/profile-store';
import { isSupabaseConfigured } from '@/lib/supabase/client';
import { trackRecordIdentityKey, upsertTrackRecords, useTrackRecord } from '@/lib/supabase/track-record';
import type { AuthUserId } from '@/lib/ids';

/**
 * Which local records have no server counterpart.
 *
 * Exported because this — not the upload — is the part worth testing: it is
 * where a mismatch between the client key and the DB's unique index would show
 * up as either endless re-uploads or silently skipped records.
 */
export function missingFromServer(
  local: InvestmentRecord[],
  server: InvestmentRecord[],
): InvestmentRecord[] {
  const onServer = new Set(server.map(trackRecordIdentityKey));
  return local.filter((record) => !onServer.has(trackRecordIdentityKey(record)));
}

/**
 * Upload any locally-held records the server does not have. Returns the number
 * uploaded (0 when already in sync).
 *
 * Throws if the write fails — the caller decides what to do about it. Nothing
 * is marked, so the next run simply tries again.
 */
export async function reconcileTrackRecord(
  userId: string,
  serverRecords: InvestmentRecord[],
): Promise<number> {
  const { investorProfile, _hasHydrated } = useProfileStore.getState();

  // See the ordering hazard above.
  if (!_hasHydrated) return 0;

  const missing = missingFromServer(investorProfile?.investmentHistory ?? [], serverRecords);
  if (missing.length === 0) return 0;

  return upsertTrackRecords(userId, missing);
}

/**
 * Mount-once hook for the investor shell.
 *
 * Failure is reported, not shown: this is a background repair the investor did
 * not ask for, and it retries on the next launch. It is NOT silent — the
 * original bug was precisely a `silent: true` fire-and-forget, so a failure
 * here reaches the error reporter, where a repeat across users reads as
 * systemic.
 */
export function useTrackRecordReconciliation(userId: AuthUserId | undefined): void {
  const hasHydrated = useProfileStore((s) => s._hasHydrated);
  const { data: serverRecords, isSuccess } = useTrackRecord(
    mode.showSampleData ? undefined : userId,
  );

  // One attempt per mount. Without this the upload's own invalidation of the
  // track-record query would re-run the effect and upload again.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    if (!userId || !hasHydrated || !isSuccess || mode.showSampleData || !isSupabaseConfigured()) {
      return;
    }

    attempted.current = true;

    void (async () => {
      try {
        const uploaded = await reconcileTrackRecord(userId, serverRecords ?? []);
        if (uploaded > 0 && __DEV__) {
          console.log(`[trackRecord] reconciled ${uploaded} device-only record(s) to the server`);
        }
      } catch (err) {
        // Allow a retry within this session if something transient failed.
        attempted.current = false;
        reportError(err, { context: 'useTrackRecordReconciliation', level: 'warning' });
      }
    })();
  }, [userId, hasHydrated, isSuccess, serverRecords]);
}
